import React, { useState, useMemo, useEffect } from 'react';
import './InPersonMeetingParticipantsModal.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalHeader, ModalBody, Button } from 'reactstrap';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { useStateSelector } from '../../../../store/selectors';
import { USER_ROLES } from '../../../../utils/constants';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { EngagementInPersonMeetingParticipant } from '../../../../slices/marketplace/meetings/models/engagement-in-person-meeting-participant';
import { InPersonMeetingParticipantSource } from '../../../../slices/marketplace/meetings/models/enums/in-person-meeting-participant-source.enum';
import { MarketplaceBuyerParticipantContract } from '../../../../slices/marketplace/meetings/models/marketplace-buyer-participant-contract';
import AddParticipantFormPopup, { AddParticipantFormValues } from './AddParticipantFormPopup';
import Helpers from '../../../../utils/helper';
import { MeetingFormat } from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { EngagementMeetingStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-status.enum';

interface InPersonMeetingParticipantsModalProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    meetingId: string;
    isReadOnly?: boolean;
    meetingFormat?: MeetingFormat;
    meetingStartDate?: Date | string;
    meetingStatus?: EngagementMeetingStatus;
}

const InPersonMeetingParticipantsModal = (
    props: InPersonMeetingParticipantsModalProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const party = useCurrentMarketplaceParty(props.engagementId);

    const canManageGlobalParticipants =
        party !== TimelineParty.Buyer ||
        summary?.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER;

    const isAfterMeetingStart =
        props.meetingStartDate != null &&
        new Date() > new Date(props.meetingStartDate);

    const isMeetingCompleted = props.meetingStatus === EngagementMeetingStatus.Completed;

    const isParticipantsReadOnly = isAfterMeetingStart || isMeetingCompleted;

    const [isAddParticipantPopupVisible, setIsAddParticipantPopupVisible] =
        useState(false);
    const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
        useState(false);
    const [participantToDelete, setParticipantToDelete] =
        useState<EngagementInPersonMeetingParticipant | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [localParticipants, setLocalParticipants] = useState<EngagementInPersonMeetingParticipant[]>([]);
    const [buyerParticipants, setBuyerParticipants] = useState<MarketplaceBuyerParticipantContract[]>([]);

    const fetchParticipants = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants`
            );
            if (response.status === 200) {
                setLocalParticipants(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch participants:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBuyerParticipants = async () => {
        try {
            const response = await axios.get('/api/Marketplace/MarketplaceBuyerParticipants');
            if (response.status === 200) {
                setBuyerParticipants(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch buyer participants:', error);
        }
    };

    useEffect(() => {
        if (props.isVisible && props.engagementId && props.meetingId) {
            fetchParticipants();
            if (party === TimelineParty.Buyer) {
                fetchBuyerParticipants();
            }
        }
    }, [props.isVisible, props.engagementId, props.meetingId]);

    const sortedParticipants = useMemo(() => {
        if (!localParticipants || localParticipants.length === 0) {
            return [];
        }

        const getRank = (p: EngagementInPersonMeetingParticipant): number => {
            if (p.party === TimelineParty.Seller && p.source === InPersonMeetingParticipantSource.TeamMember) return 0;
            if (p.party === TimelineParty.Buyer && p.source === InPersonMeetingParticipantSource.TeamMember) return 1;
            return 2;
        };

        return [...localParticipants].sort((a, b) => getRank(a) - getRank(b));
    }, [localParticipants]);

    const canDeleteParticipant = (
        participant: EngagementInPersonMeetingParticipant
    ): boolean => {
        if (party !== TimelineParty.Buyer && party !== TimelineParty.Seller) {
            return false;
        }
        if (participant.source === InPersonMeetingParticipantSource.TeamMember) {
            return false;
        }
        return participant.party === party;
    };

    const handleAddParticipant = async (values: AddParticipantFormValues) => {
        try {
            setIsSubmitting(true);

            let response;
            if (values.shareToList) {
                response = await axios.post(
                    '/api/Marketplace/MarketplaceBuyerParticipants',
                    {
                        name: values.name,
                        role: values.role,
                        engagementId: props.engagementId,
                        meetingProposalId: props.meetingId,
                    }
                );
            } else {
                response = await axios.post(
                    `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants`,
                    {
                        name: values.name,
                        role: values.role,
                    }
                );
            }

            if (response.status === 200 || response.status === 201) {
                createNotification('Participant added successfully', 'success');
                setIsAddParticipantPopupVisible(false);
                await Promise.all([
                    fetchParticipants(),
                    fetchBuyerParticipants(),
                ]);
            } else {
                createNotification('Failed to add participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to add participant', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const initialSelectedBuyerIds = useMemo(() => {
        if (party !== TimelineParty.Buyer || buyerParticipants.length === 0) return [];
        const linkedIds = new Set(
            localParticipants
                .map((p) => p.buyerParticipantId)
                .filter((id): id is number => id != null)
        );
        return buyerParticipants
            .filter((bp) => linkedIds.has(bp.id))
            .map((bp) => bp.id);
    }, [buyerParticipants, localParticipants, party]);

    const handleDeleteBuyerParticipant = async (id: number) => {
        try {
            const response = await axios.delete(`/api/Marketplace/MarketplaceBuyerParticipants/${id}`);
            if (response.status === 200 || response.status === 204) {
                createNotification('Participant deleted from all meetings', 'success');
                await Promise.all([fetchParticipants(), fetchBuyerParticipants()]);
            } else {
                createNotification('Failed to delete participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to delete participant', 'error');
        }
    };

    const handleUpdateBuyerParticipant = async (participant: { id: number; name: string; role: string }) => {
        try {
            const response = await axios.put('/api/Marketplace/MarketplaceBuyerParticipants', participant);
            if (response.status === 200 || response.status === 204) {
                createNotification('Participant updated successfully', 'success');
                await Promise.all([fetchParticipants(), fetchBuyerParticipants()]);
            } else {
                createNotification('Failed to update participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to update participant', 'error');
        }
    };

    const handleSyncBuyerParticipants = async (selectedIds: number[]) => {
        try {
            const response = await axios.put(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/BuyerParticipants/Sync`,
                selectedIds
            );
            if (response.status === 200 || response.status === 204) {
                createNotification('Participants updated successfully', 'success');
                await Promise.all([fetchParticipants(), fetchBuyerParticipants()]);
            } else {
                createNotification('Failed to update participants', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to update participants', 'error');
        }
    };

    const handleDeleteClick = (
        participant: EngagementInPersonMeetingParticipant
    ) => {
        setParticipantToDelete(participant);
        setIsDeleteConfirmationVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!participantToDelete) return;

        try {
            setIsDeleting(true);
            const response = await axios.delete(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/${participantToDelete.id}`
            );

            if (response.status === 200 || response.status === 204) {
                createNotification('Participant deleted successfully', 'success');
                setIsDeleteConfirmationVisible(false);
                setParticipantToDelete(null);
                await fetchParticipants();
            } else {
                createNotification('Failed to delete participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to delete participant', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const getPartyDisplayName = (partyType: TimelineParty): string => {
        switch (partyType) {
            case TimelineParty.Buyer:
                return 'Buyer';
            case TimelineParty.Seller:
                return 'Seller';
            default:
                return 'Unknown';
        }
    };

    const getColumns = () => {
        const columns: any[] = [
            {
                dataField: 'name',
                text: 'Name',
                headerClasses: 'name-column-header',
                sort: true,
            },
            {
                dataField: 'party',
                text: 'Representing',
                headerClasses: 'representing-column-header',
                sort: true,
                formatter: (cell: TimelineParty) => getPartyDisplayName(cell),
            },
            {
                dataField: 'role',
                text: 'Title/Role',
                headerClasses: 'role-column-header',
                sort: true,
            },
        ];

        if ((party === TimelineParty.Buyer || party === TimelineParty.Seller) && !props.isReadOnly && !isParticipantsReadOnly) {
            columns.push({
                dataField: 'actions',
                text: 'Actions',
                sort: false,
                formatter: (
                    _: any,
                    row: EngagementInPersonMeetingParticipant
                ) => {
                    if (!canDeleteParticipant(row)) {
                        return null;
                    }
                    return (
                        <div className="action-icons">
                            <FontAwesomeIcon
                                className="delete-button"
                                icon={faTrashCan}
                                onClick={() => handleDeleteClick(row)}
                                title="Delete participant"
                            />
                        </div>
                    );
                },
            });
        }

        return columns;
    };

    return (
        <div className="in-person-meeting-participants-modal">
            <Rodal
                width={800}
                height={600}
                visible={
                    props.isVisible &&
                    !isAddParticipantPopupVisible &&
                    !isDeleteConfirmationVisible
                }
                animation={'slideDown'}
                onClose={props.onClose}
                center>
                <ModalHeader>
                    {props.meetingFormat === MeetingFormat.Virtual
                        ? 'Virtual meeting participants'
                        : 'In-person meeting participants'}
                </ModalHeader>
                <ModalBody className="in-person-meeting-participants-modal-body">
                    {isLoading ? (
                        Helpers.renderTableLoader()
                    ) : (
                        <BootstrapTable
                            bootstrap4
                            striped
                            bordered
                            keyField="id"
                            data={sortedParticipants}
                            columns={getColumns()}
                            noDataIndication="No participants found"
                            rowStyle={{ height: '44px' }}
                        />
                    )}
                    {!props.isReadOnly && !isParticipantsReadOnly && (
                        <div className="add-participant-section">
                            <Button
                                color="primary"
                                onClick={() =>
                                    setIsAddParticipantPopupVisible(true)
                                }>
                                Add New Participant
                            </Button>
                        </div>
                    )}
                </ModalBody>
            </Rodal>

            <AddParticipantFormPopup
                isVisible={isAddParticipantPopupVisible}
                onClose={() => setIsAddParticipantPopupVisible(false)}
                onSubmit={handleAddParticipant}
                isSubmitting={isSubmitting}
                buyerParticipants={party === TimelineParty.Buyer ? buyerParticipants : undefined}
                initialSelectedBuyerIds={initialSelectedBuyerIds}
                onSyncBuyerParticipants={handleSyncBuyerParticipants}
                onDeleteBuyerParticipant={handleDeleteBuyerParticipant}
                onUpdateBuyerParticipant={handleUpdateBuyerParticipant}
                canManageGlobalParticipants={canManageGlobalParticipants}
            />

            <ConfirmationModal
                isVisible={isDeleteConfirmationVisible}
                setIsVisible={setIsDeleteConfirmationVisible}
                header="Confirmation"
                confirmationText={`Are you sure you want to remove '${participantToDelete?.name}'?`}
                onConfirm={handleDeleteConfirm}
                nextButtonText="Confirm"
                cancelButtonText="Back"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default InPersonMeetingParticipantsModal;
